import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { api } from "../lib/api";

/**
 * Phase 43 — Shared modal to assign a transporter to N shipments/orders.
 *
 * Props:
 *   open, onClose(): dialog control
 *   kind: "shipments" | "orders"        → drives which bulk endpoint we call
 *   itemIds: string[]                   → ids to assign in bulk (1 for single)
 *   onAssigned({assigned, skipped}): notified after a successful call
 *
 * The dropdown lists only ACTIVE transporters (soft-deleted carriers
 * remain valid targets in the DB for historic reads but must not be
 * pickable for new assignments — the backend also rejects them).
 */
export default function AssignTransporterModal({
  open,
  onClose,
  kind,
  itemIds,
  onAssigned,
}) {
  const [transporters, setTransporters] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected("");
    api
      .get("/admin/transporters", { params: { active_only: true } })
      .then(({ data }) => setTransporters(Array.isArray(data) ? data : []))
      .catch(() => toast.error("No se pudieron cargar los transportistas"));
  }, [open]);

  const count = itemIds?.length || 0;
  const label = kind === "shipments" ? "envío" : "pedido";
  const labelPlural = kind === "shipments" ? "envíos" : "pedidos";

  const submit = async (unassign = false) => {
    if (count === 0) return;
    if (!unassign && !selected) {
      toast.error("Selecciona un transportista");
      return;
    }
    setBusy(true);
    try {
      const bodyKey = kind === "shipments" ? "shipment_ids" : "order_ids";
      const path =
        kind === "shipments"
          ? "/admin/shipments/bulk-assign-transporter"
          : "/admin/orders/bulk-assign-transporter";
      const { data } = await api.post(path, {
        [bodyKey]: itemIds,
        transporter_id: unassign ? null : selected,
      });
      const assigned = data?.assigned || 0;
      const skipped = data?.skipped || [];
      if (assigned > 0) {
        toast.success(
          unassign
            ? `${assigned} ${assigned === 1 ? label : labelPlural} desasignado${assigned === 1 ? "" : "s"}`
            : `${assigned} ${assigned === 1 ? label : labelPlural} asignado${assigned === 1 ? "" : "s"}`,
        );
      }
      if (skipped.length > 0) {
        toast.warning(
          `${skipped.length} ${skipped.length === 1 ? label : labelPlural} sin procesar (estado no válido o no encontrado)`,
        );
      }
      onAssigned?.(data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al asignar");
    } finally {
      setBusy(false);
    }
  };

  const modalId =
    kind === "shipments" ? "admin-shipment-assign-modal" : "admin-order-assign-modal";
  const selectId =
    kind === "shipments" ? "admin-shipment-assign-select" : "admin-order-assign-select";

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onClose())}>
      <DialogContent className="max-w-md" data-testid={modalId}>
        <DialogHeader>
          <DialogTitle>
            Asignar transportista a {count} {count === 1 ? label : labelPlural}
          </DialogTitle>
        </DialogHeader>

        {transporters.length === 0 ? (
          <div className="py-4 text-sm text-neutral-500">
            No hay transportistas activos. Crea uno en{" "}
            <a href="/admin/transportistas" className="text-[#00953B] font-semibold">
              /admin/transportistas
            </a>{" "}
            primero.
          </div>
        ) : (
          <div className="space-y-3">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid={selectId}>
                <SelectValue placeholder="Selecciona un transportista…" />
              </SelectTrigger>
              <SelectContent>
                {transporters.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {t.coverage_area ? (
                      <span className="text-xs text-neutral-500 ml-2">
                        {t.coverage_area}
                      </span>
                    ) : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-500">
              Solo se procesarán los {labelPlural} en un estado válido; el resto
              se saltará con un aviso.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => submit(true)}
            disabled={busy || count === 0}
            data-testid={`${kind}-assign-modal-unassign-btn`}
            className="text-neutral-600"
          >
            Desasignar
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid={`${kind}-assign-modal-cancel-btn`}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => submit(false)}
            disabled={busy || !selected || transporters.length === 0}
            data-testid={`${kind}-assign-modal-confirm-btn`}
            className="bg-[#00953B] hover:bg-[#007a30] text-white"
          >
            {busy ? "Asignando…" : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
